import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import KYCBanner from 'components/KYCBanner';
import {
    MdEuro,
    MdOutlinePeopleOutline,
    MdOutlineShoppingBasket,
} from 'react-icons/md';
import { TbPigMoney } from 'react-icons/tb';
import MiniStatistics from 'components/card/MiniStatistics';
import ChartBar3Period from '../dashboard/components/ChartBar3Period';
import ReactApexChart from 'react-apexcharts';

// ─── types ────────────────────────────────────────────────────────────────────

type StatusEntry = { _id: { period: string; status: string }; sum: number };

// ─── stacked leads chart ──────────────────────────────────────────────────────

function buildStackedChart(
    data: StatusEntry[],
    period: 'monthly' | 'quarterly' | 'yearly'
) {
    const rawKey = (p: string) => {
        const [m, y] = p.split('-').map(Number);
        switch (period) {
            case 'quarterly': return `Q${Math.ceil(m / 3)}-${y}`;
            case 'yearly':    return `${y}`;
            default:          return `${m}-${y}`;
        }
    };

    const periodSet = new Set<string>();
    data.forEach((d) => periodSet.add(rawKey(d._id.period)));
    const periods = Array.from(periodSet).sort((a, b) => {
        const parseKey = (k: string) => {
            if (k.startsWith('Q')) { const [q, y] = k.slice(1).split('-').map(Number); return y * 10 + q; }
            if (k.includes('-')) { const [m, y] = k.split('-').map(Number); return y * 100 + m; }
            return Number(k);
        };
        return parseKey(a) - parseKey(b);
    });

    const labels = periods.map((p) => {
        if (p.startsWith('Q') || !p.includes('-')) return p;
        const [m, y] = p.split('-').map(Number);
        return `${String(m).padStart(2, '0')}/${y}`;
    });

    const sumFor = (periodKey: string, status: string) =>
        data
            .filter((d) => rawKey(d._id.period) === periodKey && d._id.status === status)
            .reduce((s, d) => s + d.sum, 0);

    return {
        series: [
            { name: 'Accepté',     data: periods.map((p) => sumFor(p, 'approved')) },
            { name: 'En attente',  data: periods.map((p) => sumFor(p, 'pending') + sumFor(p, 'conflict')) },
            { name: 'Non facturé', data: periods.map((p) => sumFor(p, 'rejected')) },
        ],
        categories: labels,
    };
}

function LeadsStackedChart({ data }: { data: StatusEntry[] }) {
    const [period, setPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');

    const chart = useMemo(() => {
        if (!data?.length) return null;
        return buildStackedChart(data, period);
    }, [data, period]);

    const tabs = [
        { key: 'monthly'   as const, label: 'Mensuel' },
        { key: 'quarterly' as const, label: 'Trimestriel' },
        { key: 'yearly'    as const, label: 'Annuel' },
    ];

    return (
        <div className="relative rounded-xl border border-leadrs-stroke bg-white p-5" style={{ maxHeight: 340 }}>
            <p className="font-geist text-sm font-semibold text-leadrs-text">Leads generes</p>
            <div className="absolute right-4 top-4 flex gap-3">
                {tabs.map(({ key, label }) => (
                    <button
                        key={key}
                        className={`font-geist text-xs transition-colors ${period === key ? 'font-semibold text-leadrs-brand underline' : 'text-leadrs-text-soft hover:text-leadrs-text'}`}
                        onClick={() => setPeriod(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>
            {chart ? (
                <ReactApexChart
                    type="bar"
                    height={250}
                    series={chart.series}
                    options={{
                        chart: { type: 'bar', stacked: true, toolbar: { tools: { download: false } } },
                        colors: ['#22c55e', '#1C0F53', '#ef4444'],
                        plotOptions: { bar: { horizontal: false, columnWidth: '55%', borderRadius: 5, borderRadiusApplication: 'end' } },
                        dataLabels: { enabled: false },
                        stroke: { show: true, width: 2, colors: ['transparent'] },
                        xaxis: { categories: chart.categories },
                        yaxis: { labels: { formatter: (v: any) => parseInt(v) } },
                        legend: { position: 'top' },
                    } as any}
                />
            ) : (
                <div className="flex h-60 items-center justify-center">
                    <p className="font-geist text-sm text-leadrs-text-soft">Chargement...</p>
                </div>
            )}
        </div>
    );
}

// ─── monthly table ────────────────────────────────────────────────────────────

type PaidEntry = { _id: string; sum: number; amount: number };

// Same categories as "Mes leads" (leadrs-back services/sellerLeadCategory.ts), counted per month
type LeadCategory = 'no_match' | 'unsold' | 'pending' | 'approved' | 'rejected' | 'paid';
type CategoryEntry = { _id: { period: string; category: LeadCategory }; sum: number };

const CATEGORY_COLUMNS: { key: LeadCategory; label: string; className: string }[] = [
    { key: 'no_match', label: 'En cours de traitement', className: 'text-leadrs-text-soft' },
    { key: 'unsold',   label: 'Invendus',               className: 'text-leadrs-text-soft' },
    { key: 'pending',  label: 'Vérification',           className: 'text-leadrs-text-soft' },
    { key: 'approved', label: 'Approuvés',              className: 'font-semibold text-leadrs-green-focus' },
    { key: 'paid',     label: 'Payés',                  className: 'font-semibold text-leadrs-green-focus' },
    { key: 'rejected', label: 'Refusés',                className: 'font-semibold text-leadrs-red-focus' },
];

type TableRow = {
    period: string;
    total: number;
    counts: Record<LeadCategory, number>;
    paidAmount: number;
    rate: number | null;
};

const emptyCounts = (): Record<LeadCategory, number> => ({ no_match: 0, unsold: 0, pending: 0, approved: 0, rejected: 0, paid: 0 });

const periodKey = (p: string) => {
    const [m, y] = p.split('-').map(Number);
    return `${String(m).padStart(2, '0')}/${y}`;
};

function buildTable(data: CategoryEntry[], paidData: PaidEntry[]): TableRow[] {
    const paidAmountByPeriod = new Map<string, number>();
    paidData.forEach((d) => {
        const key = periodKey(d._id);
        paidAmountByPeriod.set(key, (paidAmountByPeriod.get(key) ?? 0) + (d.amount ?? 0));
    });

    const map = new Map<string, Record<LeadCategory, number>>();
    data.forEach((d) => {
        const key = periodKey(d._id.period);
        const counts = map.get(key) ?? emptyCounts();
        counts[d._id.category] += d.sum;
        map.set(key, counts);
    });

    return Array.from(map.entries())
        .map(([period, counts]) => {
            const sold = counts.approved + counts.paid;
            const decided = sold + counts.rejected;
            return {
                period,
                total: Object.values(counts).reduce((s, n) => s + n, 0),
                counts,
                paidAmount: paidAmountByPeriod.get(period) ?? 0,
                rate: decided > 0 ? Math.round((sold / decided) * 100) : null,
            };
        })
        .sort((a, b) => {
            const toTs = (s: string) => { const [mm, yy] = s.split('/').map(Number); return yy * 100 + mm; };
            return toTs(b.period) - toTs(a.period);
        });
}

function LeadsTable({ data, paidData }: { data: CategoryEntry[]; paidData: PaidEntry[] }) {
    const rows = useMemo(() => (data?.length ? buildTable(data, paidData ?? []) : []), [data, paidData]);
    if (!rows.length) return null;

    return (
        <div className="mt-5 rounded-xl border border-leadrs-stroke bg-white p-5">
            <p className="mb-3 font-geist text-sm font-semibold text-leadrs-text">Récapitulatif mensuel</p>
            <div className="overflow-x-auto">
                <table className="w-full text-left font-geist text-sm">
                    <thead>
                        <tr className="border-b border-leadrs-stroke">
                            <th className="px-4 py-2 font-medium text-leadrs-text-soft">Période</th>
                            <th className="px-4 py-2 text-right font-medium text-leadrs-text-soft">Générés</th>
                            {CATEGORY_COLUMNS.map((c) => (
                                <th key={c.key} className="px-4 py-2 text-right font-medium text-leadrs-text-soft">{c.label}</th>
                            ))}
                            <th className="px-4 py-2 text-right font-medium text-leadrs-green-focus">Payés (€)</th>
                            <th className="px-4 py-2 text-right font-medium text-leadrs-text-soft">% Acceptation</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.period} className="border-t border-leadrs-stroke hover:bg-leadrs-bg">
                                <td className="px-4 py-2 font-medium text-leadrs-text capitalize">{row.period.replace(/^(\d{2})\/(\d{4})$/, (_, m, y) => new Date(`${y}-${m}-01`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }))}</td>
                                <td className="px-4 py-2 text-right text-leadrs-text">{row.total}</td>
                                {CATEGORY_COLUMNS.map((c) => (
                                    <td key={c.key} className={`px-4 py-2 text-right ${c.className}`}>{row.counts[c.key]}</td>
                                ))}
                                <td className="px-4 py-2 text-right font-semibold text-leadrs-green-focus">
                                    {row.paidAmount > 0 ? `${row.paidAmount.toFixed(2)} €` : '—'}
                                </td>
                                <td className="px-4 py-2 text-right">
                                    {row.rate !== null ? (
                                        <span className={`font-semibold ${row.rate >= 70 ? 'text-leadrs-green-focus' : row.rate >= 40 ? 'text-leadrs-yellow-focus' : 'text-leadrs-red-focus'}`}>
                                            {row.rate}%
                                        </span>
                                    ) : (
                                        <span className="text-leadrs-text-soft">—</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── main page ────────────────────────────────────────────────────────────────

const SellerReporting = () => {
    const [user, setUser] = useState(null);
    const [kpis, setKpis] = useState(null);
    const [landingPageStats, setLandingPageStats] = useState<{ _id: string; count: number }[]>([]);

    const initUser = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                setUser(res.data);
                if (res.data && res.data.type !== 'seller') {
                    window.location.href = '/my-request';
                }
            })
            .catch((err) => {
                console.log('No user info found', err);
            });
    };

    const initSellerKPI = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/seller-kpi`, config)
            .then((res) => {
                setKpis(res.data);
            })
            .catch((err) => {
                console.log('No user info found', err);
            });
    };

    const initLandingPageStats = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/seller-landing-page-stats`, config)
            .then((res) => {
                setLandingPageStats(res.data);
            })
            .catch((err) => {
                console.log('Could not load landing page stats', err);
            });
    };

    useEffect(() => {
        if (
            !localStorage.getItem('token') ||
            localStorage.getItem('token').length < 10
        )
            window.location.href = '/auth/sign-up';
        initUser();
        initSellerKPI();
        initLandingPageStats();
    }, []);

    return (
        <div className="flex flex-col gap-6">
            <KYCBanner user={user} onUserRefresh={initUser} />

            {/* Header */}
            <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                <p className="font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">
                    Vendeur
                </p>
                <h1 className="mt-1 font-crimson text-4xl font-semibold leading-tight text-leadrs-text">
                    Reporting
                </h1>
                <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                    L&apos;apercu complet de vos ventes, de vos paiements et du solde restant a vendre.
                </p>
            </div>

            {/* KPI stats */}
            <div className="grid grid-cols-2 gap-5 md:grid-cols-2 xl:grid-cols-4">
                <MiniStatistics
                    name="Leads generes"
                    value={kpis ? `${kpis?.leadsCount}` : '-'}
                    icon={<MdOutlinePeopleOutline color="#1C0F53" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Offres creees"
                    value={kpis ? `${kpis.createdOffers}` : '-'}
                    icon={<MdOutlineShoppingBasket color="#1C0F53" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Offres rejointes"
                    value={kpis ? `${kpis?.acceptedOffers}` : '-'}
                    icon={<TbPigMoney color="#1C0F53" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Prix moyen / lead"
                    value={kpis ? `${kpis.averagePricePerOffer} €` : '-'}
                    icon={<MdEuro color="#1C0F53" />}
                    iconBg="bg-lightPrimary"
                />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <ChartBar3Period
                    title="Vos offres"
                    data={kpis?.createdOrderSparklineData}
                    calculateCumulativeSum={true}
                />
                <LeadsStackedChart data={kpis?.leadGeneratedByStatusData ?? []} />
            </div>

            <LeadsTable data={kpis?.leadsByCategoryByPeriod ?? []} paidData={kpis?.leadGeneratedPaidByPeriod ?? []} />

            {/* Landing page analytics */}
            {landingPageStats.length > 0 && (
                <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                    <h2 className="font-crimson text-2xl font-semibold text-leadrs-text">Leads par site</h2>
                    <p className="mt-1 mb-5 font-geist text-sm text-leadrs-text-soft">
                        Quelle page de votre site envoie le plus de leads
                    </p>
                    {(() => {
                        const maxCount = Math.max(...landingPageStats.map((s) => s.count));
                        return (
                            <div className="flex flex-col gap-3">
                                {landingPageStats.map((s) => (
                                    <div key={s._id} className="rounded-xl border border-leadrs-stroke p-4">
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <svg className="h-3.5 w-3.5 shrink-0 text-leadrs-text-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                                                </svg>
                                                <span className="truncate font-geist text-sm font-medium text-leadrs-text">
                                                    {s._id.replace(/^https?:\/\//, '')}
                                                </span>
                                            </div>
                                            <span className="shrink-0 font-geist text-sm font-semibold text-leadrs-brand">
                                                {s.count} lead{s.count > 1 ? 's' : ''}
                                            </span>
                                        </div>
                                        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-leadrs-bg">
                                            <div
                                                className="h-full rounded-full bg-leadrs-brand"
                                                style={{ width: `${Math.round((s.count / maxCount) * 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                </div>
            )}
        </div>
    );
};

export default SellerReporting;
